/**
 * src/lib/hooks/usePendingReviews.jsx — TICKET-1098.
 *
 * ONE source of "quotes waiting for review" for every surface that flags them:
 * the Pipeline tab badge, the Pipeline "Por revisar" filter + card highlight, the
 * Tasks "Revisar" lane count and the app-open toast. One query, shared through a
 * provider, so the counts can never disagree with each other.
 *
 * Scope (CEO 2026-10-05): every non-archived quote in `pending_review`, visible to
 * the approver roles only. `quotes` is not in the realtime publication, so this
 * refetches on focus/visibility, on a slow interval, and when a caller asks
 * (`reload` — AppShell calls it when the quote overlay closes after an approve).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';

export const REVIEW_ROLES = ['ceo', 'admin', 'pmo'];
const POLL_MS = 2 * 60 * 1000;
const EMPTY = { reviews: [], closedReviews: [], count: 0, dealIds: new Set(), byDeal: new Map(), enabled: false, loaded: false, reload: () => {}, reviewFilter: false, setReviewFilter: () => {} };

/** Raw quote rows → the view shape. Pure, exported for tests. */
// TICKET-1108: a quote whose deal is closed (won / lost / cancelled — the stage's
// is_terminal flag, never the mixed-language status text) or archived is no longer
// "waiting for you": on 2026-10-05, 15 of 17 pending quotes sat on closed deals.
// They are NOT approved for the CEO (a won deal may still have an unsigned or
// unapproved part) — they leave the counts and stay reachable in a closed section.
export function shapeReviews(rows) {
  return (rows || []).map((q) => {
    const deal = q.crm_deals || null;
    const closed = Boolean(deal && (deal.archived_at || deal.stages?.is_terminal));
    return {
      id: q.id,
      quoteNumber: q.quote_number ?? null,
      dealId: q.deal_id || null,
      name: deal?.opportunity?.trim() || null,
      closed,
      dealStage: deal?.stages?.slug || null,
    };
  });
}

/** "#99 — MOON" / "#99" — the label every surface shows. */
export const reviewLabel = (r) => `#${r.quoteNumber ?? '?'}${r.name ? ` — ${r.name}` : ''}`;

const Ctx = createContext(EMPTY);

// `refreshKey` — any change triggers a refetch (AppShell passes the open quote id,
// so closing the quote overlay after an approve drops it from every count).
// `reviewFilter` lives here so "See all" from a toast can switch the Pipeline filter on.
export function PendingReviewsProvider({ role, refreshKey, children }) {
  const enabled = REVIEW_ROLES.includes(role);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState(false);
  // False until the first successful fetch, so a surface can show "–" instead of a
  // confident 0 while the count is unknown (Command Center decisions row).
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    // Disabled (role switched away) = unknown again, never a remembered "loaded 0".
    if (!enabled) { setReviews([]); setLoaded(false); return; }
    const { data, error } = await supabase.from('quotes')
      .select('id, quote_number, deal_id, crm_deals(opportunity, archived_at, stages(slug, is_terminal))')
      .eq('review_status', 'pending_review').is('archived_at', null)
      .order('quote_number', { ascending: false });
    // A failed refetch keeps the last good list — a flicker to 0 would hide work.
    if (!error) { setReviews(shapeReviews(data)); setLoaded(true); }
  }, [enabled]);

  useEffect(() => {
    reload();
    if (!enabled) return undefined;
    const onVisible = () => { if (document.visibilityState !== 'hidden') reload(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(onVisible, POLL_MS);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, [enabled, reload]);

  const prevKey = useRef(refreshKey);
  useEffect(() => {
    // Only on a transition to "closed" — the mount fetch above already ran.
    if (prevKey.current !== refreshKey && !refreshKey) reload();
    prevKey.current = refreshKey;
  }, [refreshKey, reload]);

  const value = useMemo(() => {
    const open = reviews.filter((r) => !r.closed);
    const closedReviews = reviews.filter((r) => r.closed);
    const byDeal = new Map();
    for (const r of open) if (r.dealId && !byDeal.has(r.dealId)) byDeal.set(r.dealId, r);
    return { reviews: open, closedReviews, count: open.length, dealIds: new Set(byDeal.keys()), byDeal, enabled, loaded, reload, reviewFilter: enabled && reviewFilter, setReviewFilter };
  }, [reviews, enabled, loaded, reload, reviewFilter]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export default function usePendingReviews() {
  return useContext(Ctx);
}
